import { LocationFeatureFlagSettingName } from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import { useUpdateMultiLocationFeatureSettingsCache } from '@frontend/api-payments-view';
import { useMutation } from '@frontend/react-query-helpers';
import { SchemaPaymentsViewsService } from '@frontend/schema';
import { useFormField, useModalControl, useAlert } from '@frontend/design-system';
import { useSelectedLocations } from '../../providers/selected-location-context';
import { PaymentsFeatureSettings } from '../../types/payment-hooks.types';
import { useLocationFeatureSettings } from './use-location-feature-settings';

interface UseLocationFeatureSwitchParams {
  setting: PaymentsFeatureSettings;
  errMsg: string;
  useConfirmation?: boolean;
  locationIds?: string[];
}
export const useLocationFeatureSwitch = ({
  setting,
  errMsg,
  useConfirmation = false,
  locationIds: locationIdsParam,
}: UseLocationFeatureSwitchParams) => {
  const alert = useAlert();

  const { locationIds: locationIdsFromUtils } = useSelectedLocations();
  const locationIds = (locationIdsParam ?? locationIdsFromUtils).filter(Boolean) as string[];

  const {
    mixedValue: featureSetting,
    isLoading,
    refetchAll,
  } = useLocationFeatureSettings(setting, {
    locationIds,
  });
  const { value: switchValue, ...switchProps } = useFormField(
    {
      value: featureSetting === true,
      type: 'switch',
    },
    [featureSetting]
  );

  const { modalProps, triggerProps } = useModalControl();

  const updateMultiLocationFeatureSettingsCache = useUpdateMultiLocationFeatureSettingsCache();

  const { mutate: toggleFeatureFlag, isLoading: isTogglingFeatureFlag } = useMutation(
    async (value: boolean) => {
      const requests = locationIds.map((locationId) => {
        return SchemaPaymentsViewsService.SetLocationFeatureEnabled({
          locationId,
          settingName: setting as LocationFeatureFlagSettingName,
          enabled: value,
        }).then((res) => {
          if (locationId) {
            updateMultiLocationFeatureSettingsCache(locationId, setting, value);
          }
          return res;
        });
      });
      await Promise.all(requests);
    },
    {
      onError: () => {
        alert.error(errMsg);
      },
      onSettled: () => {
        refetchAll();
      },
    }
  );

  const handleFeatureFlagToggle = () => toggleFeatureFlag(!switchValue);

  const handleOpenModal = () => triggerProps.onClick();

  return {
    featureSettingSwitchProps: {
      ...switchProps,
      value: switchValue,
      disabled: isTogglingFeatureFlag || isLoading,
      onChange: useConfirmation ? handleOpenModal : handleFeatureFlagToggle,
      indeterminate: featureSetting === 'mixed',
    },
    onModalConfirm: handleFeatureFlagToggle,
    modalProps,
  };
};
